import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { DrawerInstructionsComponent } from './components/drawer-instructions/drawer-instructions.component';
import { DrawerNotesComponent } from './components/drawer-notes/drawer-notes.component';
import { DrawerSummaryComponent } from './components/drawer-summary/drawer-summary.component';
import { DrawerPreVisitRecordForPersonNotesComponent } from './components/drawer-pre-visit-record-for-person-notes/drawer-pre-visit-record-for-person-notes.component';
import { ListConversationsComponent } from './components/list-conversations/list-conversations.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import RoutingService from './services/routing.service';
import Config from './utils/config.service';
import AngularHelperService from './services/angular-helper.service';
import AngularBroadcastHelperService from './services/angular-broadcast-helper.service';
import CalendarLocaleService from './services/calendar-locale.service';
import Session from './utils/session.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-root',
	template: `
		<div id="fullScreenContainer">
			<div id="backGroundOverlay">
				<router-outlet name="fullscreen" style="display: none;"></router-outlet>
			</div>
		</div>
		<mat-drawer-container>
			<mat-drawer-content>
				<div id="mainDrawerContainer" class="o-grid o-grid--row">
					<div id="mainBackgroundContainer" class="o-grid o-grid--fs o-grid--column c-container c-container--has-tab-bar c-bg">
						<div class="o-grid__cell--w-fixed u-no-p" *ngIf="sessionAvailable">
							<header></header>
						</div>
						<div id="mainContentContainer" class="o-grid__cell o-grid__cell--w-fit u-no-p">
							<main id="main" class="o-container">
								<ng-container *ngIf="sessionAvailable || isPublicPage">
									<router-outlet style="display: none;"></router-outlet>
									<list-conversations></list-conversations>
								</ng-container>
							</main>
						</div>
					    <!-- fake load hero image -->
					    <img id="heroImgPlaceHolder" alt="Preload image" width="1" height="1" style="visibility: hidden;" src="https://dqs3afosyxqoy.cloudfront.net/assets/minudoc/account/img/bg-hero.jpg" />
						<div id="mainFooterContainer" class="o-grid__cell o-grid__cell--w-fixed u-no-p">
							<ng-container *ngIf="sessionAvailable || isPublicPage">
								<footer></footer>
							</ng-container>
						</div>
						
						<ng-container *ngIf="sessionAvailable || isPublicPage">
							<!--not-supported>TODO ANGULAR_2: is this missing?</not-supported-->
						</ng-container>
					</div>
				</div>
				<ng-container *ngIf="sessionAvailable">
					<drawer-instructions></drawer-instructions>
					<drawer-notes></drawer-notes>
					<drawer-summary></drawer-summary>
					<drawer-pre-visit-record-for-person-notes></drawer-pre-visit-record-for-person-notes>
				</ng-container>
			</mat-drawer-content>
		</mat-drawer-container>
	`,
	standalone: true,
	imports: [
		RouterModule, CommonModule, HeaderComponent, FooterComponent, 
		ListConversationsComponent, MatSidenavModule,
		DrawerInstructionsComponent, DrawerNotesComponent, 
		DrawerSummaryComponent, DrawerPreVisitRecordForPersonNotesComponent
	],
})
export class AppComponent implements OnInit, OnDestroy {
	private routingService: RoutingService = inject(RoutingService);
	private config: Config = inject(Config);
	private angularHelperService: AngularHelperService = inject(AngularHelperService);
	private angularBroadcastHelperService: AngularBroadcastHelperService = inject(AngularBroadcastHelperService);
	private calendarLocaleService: CalendarLocaleService = inject(CalendarLocaleService);
	private session: Session = inject(Session);
	
	sessionAvailable: boolean = false;
	isPublicPage: boolean = false;
	private broadcastSubscriptions: Array<Subscription> = [];
	private resizeListenerBound: boolean = false;

	ngOnInit() {
		this.sessionAvailable = false;
		this.broadcastSubscriptions.push(
			this.angularBroadcastHelperService.onRootEvent(this.config.getBroadcastConfig().Session.LOGIN, () => {
				this.initializeAuthenticatedLayout();
			})
		);
		this.broadcastSubscriptions.push(
			this.angularBroadcastHelperService.onRootEvent(this.config.getBroadcastConfig().Session.LOGOUT, () => {
				this.sessionAvailable = false;
			})
		);
		this.routingService.getSessionData()
			.then(() => {
				this.initializeAuthenticatedLayout();
			}).catch(() => {
				if (!this.angularHelperService.isPublicPath()) {
					this.angularBroadcastHelperService.rootBroadcast(this.config.getBroadcastConfig().Session.DESTROY);
				} else {
					this.isPublicPage = true;
				}
			});
		this.initMoment();
	}

	ngOnDestroy() {
		for (var i = 0; i < this.broadcastSubscriptions.length; i++) {
			this.broadcastSubscriptions[i].unsubscribe();
		}
	}

	initializeAuthenticatedLayout() {
		this.sessionAvailable = true;
		this.isPublicPage = false;
		this.calculateVhUnit();
		if (!this.resizeListenerBound) {
			this.resizeListenerBound = true;
			window.addEventListener('resize', this.calculateVhUnit);
		}
		this.applyOriginThemeVars();
	}

	calculateVhUnit() {
		var vh = window.innerHeight * 0.01;
		document.body.style.setProperty('--vh', vh + 'px');
	}

	applyOriginThemeVars() {
		// Used by training styles to pick brand color palette.
		const origin = this.session.getSession()?.user?.origin || window.location.hostname || '';
		document.documentElement.dataset['origin'] = origin;
	}
	
	initMoment() {
		this.calendarLocaleService.initializeMomentLocales();
	}
}
